import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getWorkerStatusIntradayAlgoWorkerStatusGet,
  pauseWorkerIntradayAlgoWorkerPausePost,
  resumeWorkerIntradayAlgoWorkerResumePost,
} from "@/client-admin/services.gen";

const WORKER_STATUS_KEY = ["intraday-algo-worker-status"];

export function useWorkerStatus() {
  return useQuery({
    queryKey: WORKER_STATUS_KEY,
    queryFn: getWorkerStatusIntradayAlgoWorkerStatusGet,
    refetchInterval: 3_000,
  });
}

/**
 * A portal command is "unacknowledged" if it was issued after the latest worker
 * row — i.e. the worker hasn't written a new state since we sent the command.
 */
function isCommandPending(
  workerCreatedAt: string | null | undefined,
  portalCommandAt: string | null | undefined,
): boolean {
  if (!portalCommandAt) return false;
  if (!workerCreatedAt) return true;
  return new Date(portalCommandAt).getTime() > new Date(workerCreatedAt).getTime();
}

/** True only when the worker is confirmed PAUSED and no resume command is pending. */
export function useIsWorkerPaused() {
  const { data: workerStatus } = useWorkerStatus();
  const isPaused = workerStatus?.status === "PAUSED";
  const commandPending = isCommandPending(workerStatus?.created_at, workerStatus?.latest_portal_command_at);
  const isResuming = commandPending && workerStatus?.latest_portal_command === "RESUME";
  return isPaused && !isResuming;
}

/** Label and dot colour for the status badge; a pending command wins over the worker state. */
function getStatusDisplay(status: string | null, pendingCommand: string | null): { label: string; dotColor: string } {
  if (pendingCommand === "PAUSE") return { label: "Pausing...", dotColor: "bg-blue-500" };
  if (pendingCommand === "RESUME") return { label: "Resuming...", dotColor: "bg-blue-500" };

  switch (status) {
    case "PAUSED":
      return { label: "Paused", dotColor: "bg-yellow-500" };
    case "RUNNING":
      return { label: "Active", dotColor: "bg-green-500" };
    case null:
      return { label: "No data", dotColor: "bg-gray-400" };
    default:
      return { label: status, dotColor: "bg-gray-400" };
  }
}

export function WorkerControls() {
  const queryClient = useQueryClient();
  const { data: workerStatus } = useWorkerStatus();

  const status = workerStatus?.status ?? null;
  const noWorker = status === null;

  const pendingCommand = isCommandPending(workerStatus?.created_at, workerStatus?.latest_portal_command_at)
    ? (workerStatus?.latest_portal_command ?? null)
    : null;
  const isPausing = pendingCommand === "PAUSE";
  const isResuming = pendingCommand === "RESUME";

  const pauseMutation = useMutation({
    mutationFn: pauseWorkerIntradayAlgoWorkerPausePost,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: WORKER_STATUS_KEY }),
  });

  const resumeMutation = useMutation({
    mutationFn: resumeWorkerIntradayAlgoWorkerResumePost,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: WORKER_STATUS_KEY }),
  });

  const { label: displayStatus, dotColor } = getStatusDisplay(status, pendingCommand);

  const showResumeButton = status === "PAUSED" || isResuming;
  const pauseBusy = isPausing || pauseMutation.isPending;
  const resumeBusy = isResuming || resumeMutation.isPending;

  return (
    <div className="flex items-center gap-2.5">
      <div className="flex items-center gap-2">
        <span className={`inline-block h-2.5 w-2.5 rounded-full ${dotColor}`} />
        <div className="flex flex-col leading-none">
          <span className="text-[11px] font-semibold tracking-tight">Trading</span>
          <span className="text-[10px] text-muted-foreground">{displayStatus}</span>
        </div>
      </div>
      {showResumeButton ? (
        <Button size="sm" variant="outline" disabled={resumeBusy} onClick={() => resumeMutation.mutate()}>
          {resumeBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
          {isResuming ? "Resuming..." : "Resume"}
        </Button>
      ) : (
        <Button size="sm" variant="outline" disabled={pauseBusy || noWorker} onClick={() => pauseMutation.mutate()}>
          {pauseBusy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Pause className="mr-1 h-4 w-4" />}
          {isPausing ? "Pausing..." : "Pause"}
        </Button>
      )}
    </div>
  );
}
